import { useEffect, useRef } from "react";

import { useRouter } from "next/router";

import { analyticsService } from "@/common/services/analytics/analytics.service";
import type { Experiment, StatsigClient } from "@statsig/react-bindings";

/**
 * Experiments already reported on the current page.
 *
 * Module-level for two reasons: the handler below has to be one stable reference, because that is
 * what `client.off` needs to unsubscribe, and the dedupe has to span every component that reads the
 * same experiment on a page.
 *
 * Only ever touched in the browser — see the guard in the hook. On the server this would be shared
 * between requests, which would silence every visitor after the first.
 */
const reportedOnThisPage = new Set<string>();

const handleEvaluation = ({ experiment }: { experiment: Experiment }) => {
  // v3 equivalent of the removed `DynamicConfig.getIsExperimentActive()`:
  // experiments that aren't running shouldn't produce exposure events.
  if (!experiment.__evaluation?.is_experiment_active) {
    return;
  }

  if (reportedOnThisPage.has(experiment.name)) {
    return;
  }

  reportedOnThisPage.add(experiment.name);

  analyticsService.trackExperimentExposure(
    experiment.name,
    experiment.groupName ?? "Control"
  );
};

/**
 * Forwards Statsig experiment evaluations to our own analytics, at most once per experiment per
 * page. Used by `ExperimentsProvider`, and living apart from it only because the analytics service
 * cannot be imported from the hook every experiment is read through — its own import graph reaches
 * back into `common/hooks/experiments.hooks`, and the cycle leaves that module half-built.
 *
 * **The subscription is made while rendering, not in an effect, and that is the point.** A
 * subscriber only hears evaluations made after it subscribes. Effects flush after the whole tree has
 * rendered, while a client bootstrapped from the server has values on the *first* render — so every
 * experiment read on a server-evaluated page was evaluated before the listener existed, and its
 * exposure was dropped. It went unnoticed for as long as nothing bootstrapped: a client still
 * fetching over the network is not ready on the first render, so the evaluation that counted always
 * happened on a later one, by which time the effect had run.
 *
 * React renders a parent before its children, so subscribing here — in the provider, above every
 * reader — is early enough to hear the first one.
 */
export const useExperimentExposureTracking = (client: StatsigClient) => {
  const router = useRouter();
  const subscribed = useRef<StatsigClient | null>(null);

  /* Effects never run on the server, so this is where the browser-only guard has to be. */
  if (typeof window !== "undefined" && subscribed.current !== client) {
    subscribed.current?.off("experiment_evaluation", handleEvaluation);
    client.on("experiment_evaluation", handleEvaluation);
    subscribed.current = client;
  }

  useEffect(
    () => () => {
      subscribed.current?.off("experiment_evaluation", handleEvaluation);
      subscribed.current = null;
    },
    []
  );

  useEffect(() => {
    const handleRouteChangeStart = () => reportedOnThisPage.clear();

    router.events.on("routeChangeStart", handleRouteChangeStart);

    return () => {
      router.events.off("routeChangeStart", handleRouteChangeStart);
    };
  }, [router.events]);
};
